import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  ICON_SETS,
  ICON_SET_META,
  AVAILABLE_ICON_SETS,
  DEFAULT_ICON_SET,
  getIconForCategory,
  isValidIconSet,
} from '../config/icons';

const IconThemeContext = createContext();

const ICON_THEME_STORAGE_KEY = '@chessmaps_icon_theme';

/**
 * Hook to access the icon theme context
 * Provides current icon set, theme switching functions, and category icon getter
 */
export const useIconTheme = () => {
  const context = useContext(IconThemeContext);
  if (!context) {
    throw new Error('useIconTheme must be used within an IconThemeProvider');
  }
  return context;
};

/**
 * Provider component for icon theme management
 * Wraps the app to provide icon theming capabilities throughout
 */
export const IconThemeProvider = ({ children }) => {
  const [currentIconSet, setCurrentIconSet] = useState(DEFAULT_ICON_SET);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved icon theme preference on mount
  useEffect(() => {
    loadIconThemePreference();
  }, []);

  /**
   * Load the saved icon theme from AsyncStorage
   */
  const loadIconThemePreference = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem(ICON_THEME_STORAGE_KEY);
      if (savedTheme !== null && isValidIconSet(savedTheme)) {
        setCurrentIconSet(savedTheme);
      }
    } catch (error) {
      console.error('Error loading icon theme preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Set a new icon theme and persist to storage
   * @param {string} themeKey - The icon set key to switch to
   */
  const setIconTheme = useCallback(async (themeKey) => {
    if (!isValidIconSet(themeKey)) {
      console.warn(`Invalid icon theme: ${themeKey}. Available themes:`, AVAILABLE_ICON_SETS);
      return false;
    }

    try {
      setCurrentIconSet(themeKey);
      await AsyncStorage.setItem(ICON_THEME_STORAGE_KEY, themeKey);
      return true;
    } catch (error) {
      console.error('Error saving icon theme preference:', error);
      return false;
    }
  }, []);

  /**
   * Cycle to the next available icon theme
   */
  const cycleIconTheme = useCallback(async () => {
    const currentIndex = AVAILABLE_ICON_SETS.indexOf(currentIconSet);
    const nextIndex = (currentIndex + 1) % AVAILABLE_ICON_SETS.length;
    const nextTheme = AVAILABLE_ICON_SETS[nextIndex];
    await setIconTheme(nextTheme);
    return nextTheme;
  }, [currentIconSet, setIconTheme]);

  /**
   * Get icon for a specific category using current theme
   * @param {string} category - The hotspot category
   * @returns {string} The icon for the category
   */
  const getCategoryIcon = useCallback((category) => {
    return getIconForCategory(category, currentIconSet);
  }, [currentIconSet]);

  /**
   * Get all icons in the current set
   * @returns {object} Current icon set
   */
  const getCurrentIconSet = useCallback(() => {
    return ICON_SETS[currentIconSet] || ICON_SETS[DEFAULT_ICON_SET];
  }, [currentIconSet]);

  /**
   * Get metadata for the current icon set
   * @returns {object} Icon set metadata
   */
  const getCurrentIconSetMeta = useCallback(() => {
    return ICON_SET_META[currentIconSet] || ICON_SET_META[DEFAULT_ICON_SET];
  }, [currentIconSet]);

  const value = {
    // Current state
    currentIconSet,
    isLoading,
    
    // Theme switching
    setIconTheme,
    cycleIconTheme,
    
    // Icon getters
    getCategoryIcon,
    getCurrentIconSet,
    getCurrentIconSetMeta,
    
    // Static data
    availableIconSets: AVAILABLE_ICON_SETS,
    iconSetMeta: ICON_SET_META,
    allIconSets: ICON_SETS,
  };

  return (
    <IconThemeContext.Provider value={value}>
      {children}
    </IconThemeContext.Provider>
  );
};

export default IconThemeContext;




